import { UploadAudioResponse } from "../types";
import { getApiUrl } from '../config/api';

// audioApi.ts
interface UploadedFile {
  url: string;
  name: string;
}

interface UploadResponse {
  files: UploadedFile[];
  projectId: string;
  audioUrl: string;
}

/**
 * Saves merged audio to an existing project (better than upload which creates new project)
 *
 * @param audioBlob - The merged audio file as a Blob.
 * @param projectId - The existing project ID to update.
 * @param tracks - The audio tracks that were merged (for metadata).
 * @returns A promise that resolves to the response with finalAudioId.
 */
export async function saveMergedAudio(audioBlob: Blob, projectId: string, tracks: any[]): Promise<UploadAudioResponse> {
    // Prepare the form data
    const formData = new FormData();
    formData.append('audio', audioBlob, `merged-audio-${Date.now()}.wav`);
    formData.append('projectId', projectId);
    
    // Add metadata about the merge operation
    // formData.append('metadata', JSON.stringify({
    //   type: 'final-merged-audio',
    //   tracksCount: tracks.length.toString(),
    //   mergedAt: new Date().toISOString(),
    //   trackDetails: JSON.stringify(tracks.map(track => ({
    //     id: track.id,
    //     name: track.name,
    //     volume: track.volume.toString(),
    //     duration: track.duration.toString(),
    //     startTime: track.startTime.toString(),
    //     url: track.url
    //   })))
    // }));

    // Get authentication token
    const token = localStorage.getItem('authToken');
  
    try {
      const response = await fetch(getApiUrl('/music-generation/save-merged'), {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error('Failed to save merged audio');
      }
  
      const data = await response.json();
      console.log("Save merged audio response --> ", data);
      return data as UploadAudioResponse;
    } catch (error) {
      console.error('Error saving merged audio:', error);
      throw error;
    }
}

/**
 * Uploads an audio blob to the backend and returns the URL of the uploaded audio.
 *
 * @param audioBlob - The audio file as a Blob.
 * @param projectId - Optional project ID for replacing audio in an existing project.
 * @returns A promise that resolves to the URL string if successful, or null if an error occurs.
 */
export async function uploadAudio(audioBlob: Blob, projectId?: string):Promise<UploadAudioResponse> {
    // Prepare the form data
    const formData = new FormData();
    console.log("formData--> ", typeof(audioBlob));
    formData.append('audio', audioBlob); // Adjust file name and MIME type as needed
    
    // Add project ID if provided (for audio replacement)
    if (projectId) {
      formData.append('projectId', projectId);
    }

    // Get authentication token
    const token = localStorage.getItem('authToken');
  
    try {
      // Replace with your actual backend URL
      const response = await fetch(getApiUrl('/create-content/upload-audio'), {
        method: 'POST',
        headers: {
          // 🔒 ADD AUTHENTICATION HEADER
          ...(token && { 'Authorization': `Bearer ${token}` }),
          // Note: Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error('Failed to upload audio');
      }
  
      // Expect the backend to return a JSON object with a property like { url: 'https://...' }
      const data = await response.json();
      console.log("Audio api reponse --> ", data );
      return data as Promise<UploadAudioResponse> ;
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw error;
    }
  }
  
/**
 * Uploads an audio file with optional auto-splitting for long files
 * Uses music-generation upload endpoint with auto-split capability
 *
 * @param audioFile - The audio file as a File object.
 * @param projectId - The project ID to upload to.
 * @param enableAutoSplit - Whether to enable automatic track splitting for files > 20min.
 * @returns A promise that resolves to the upload response.
 */
export async function uploadAudioWithAutoSplit(
  audioFile: File, 
  projectId: string, 
  enableAutoSplit: boolean = false
): Promise<UploadAudioResponse> {
  // Prepare the form data
  const formData = new FormData();
  formData.append('audio', audioFile);
  formData.append('projectId', projectId);
  formData.append('enableAutoSplit', enableAutoSplit ? 'true' : 'false');

  // Get authentication token
  const token = localStorage.getItem('authToken');

  try {
    const response = await fetch(getApiUrl('/music-generation/upload-audio'), {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload audio');
    }

    const data = await response.json();
    console.log("Audio upload with auto-split response --> ", data);
    return data as UploadAudioResponse;
  } catch (error) {
    console.error('Error uploading audio with auto-split:', error);
    throw error;
  }
}
